import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import auditTrailService, { AuditTrail as AuditTrailType, AuditTrailFilters } from '../services/auditTrailService';
import DateRangePicker from '../components/analytics/DateRangePicker';
import { DateRange } from '../services/analyticsService';
import AuditTrailDetailModal from '../components/AuditTrailDetailModal';

const AuditTrail: React.FC = () => {
  const { selectedClientId } = useUser();
  const [auditTrails, setAuditTrails] = useState<AuditTrailType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  
  // Filters
  const [filters, setFilters] = useState<AuditTrailFilters>({
    client_id: selectedClientId || 0,
    limit: 25,
    skip: 0,
  });
  
  // Date range
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30); // last 30 days
    return {
      date_range: 'last_30_days',
      startDate: start,
      endDate: end
    };
  });
  
  // Modal state
  const [selectedAuditTrail, setSelectedAuditTrail] = useState<AuditTrailType | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (selectedClientId) {
      setFilters(prev => ({ ...prev, client_id: selectedClientId }));
      fetchAuditTrails();
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedClientId) {
      fetchAuditTrails();
    }
  }, [paginationModel, filters]);

  const fetchAuditTrails = async () => {
    if (!selectedClientId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters: AuditTrailFilters = {
        ...filters,
        client_id: selectedClientId,
        limit: paginationModel.pageSize,
        skip: paginationModel.page * paginationModel.pageSize,
        start_date: dateRange.startDate?.toISOString(),
        end_date: dateRange.endDate?.toISOString(),
      };
      
      const response = await auditTrailService.getAuditTrails(currentFilters);
      setAuditTrails(response.audit_trails);
      setTotalCount(response.total_count);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch audit trails');
      console.error('Error fetching audit trails:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page when date range changes
  };


  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleViewDetails = (auditTrail: AuditTrailType) => {
    setSelectedAuditTrail(auditTrail);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAuditTrail(null);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'success';
      case 'update':
        return 'warning';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'change_date',
      headerName: 'Date',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'area',
      headerName: 'Area',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          size="small" 
          variant="outlined"
        />
      ),
    },
    {
      field: 'entity',
      headerName: 'Entity',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.row.entity_type}: {params.row.entity_id}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={getActionColor(params.value) as any}
        />
      ),
    },
    {
      field: 'user_name',
      headerName: 'User',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || 'System'}
        </Typography>
      ),
    },
    {
      field: 'source_system',
      headerName: 'Source',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (!selectedClientId) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Please select a client to view audit trails.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>
          Audit Trail
        </Typography>
        <DateRangePicker 
          dateRange={dateRange} 
          onDateRangeChange={handleDateRangeChange} 
        />
      </Box>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View system change history and activity logs for your organization.
      </Typography>

      {/* Results */}
      <Paper sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
        {error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <DataGrid
            rows={auditTrails}
            columns={columns}
            rowCount={totalCount}
            paginationMode="server"
            onPaginationModelChange={handlePaginationModelChange}
            paginationModel={paginationModel}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'change_date', sort: 'desc' }],
              },
              density: 'compact',
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.id}
            autoHeight={false}
            disableColumnMenu={false}
            disableColumnFilter={false}
            disableColumnSelector={false}
            disableDensitySelector={false}
            sx={{
              '& .MuiDataGrid-cell': {
                padding: '0 16px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center'
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '0 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No audit trail records found for the selected criteria.
                  </Typography>
                </Box>
              ),
            }}
          />
        )}
      </Paper>

      {/* Detail Modal */}
      {selectedAuditTrail && (
        <AuditTrailDetailModal
          open={modalOpen}
          onClose={handleCloseModal}
          auditTrail={selectedAuditTrail}
        />
      )}
    </Box>
  );
};

export default AuditTrail;